import React, { useEffect, useMemo, useRef, useState } from "react";
import { Post, Upload, EXCLUSION_REASON_LABELS } from "../models/post";
import { PostDataService, getPostConflict, isPastRestoreWindow } from "../services/postDataService";
import { makeBaseName, putImage, putModifiedVariant } from "../services/uploadHelpers";
import { useAppState } from "../context/AppStateContext";
import HelpAsk from "../help/HelpAsk";
import ConfirmationModal from "./ConfirmationModal";
import AiInsightsPanel from "./AiInsightsPanel";
import FixTypos from "./FixTypos";
import SupportRef, { postRef } from "./SupportRef";
import PhotoRedactor from "./PhotoRedactor";
import DeletedPostImages from "./DeletedPostImages";
import { WeekNavPill } from "./report/WeekNavigator";
import { CustomRangePill } from "./report/PeriodNavigator";
import { DateShortcut } from "./SidebarFilters";
import { getMonday, getFriday, toDateString } from "../services/dateUtils";
import { postStamp } from "../services/sharedService";
import { useOfflineActionTitle } from "../hooks/useConnectivity";

// AI insights on the post overlay is hidden until the functionality is
// complete — flip to true to restore the wand button + panel.
const SHOW_AI_INSIGHTS = false;

// data.post.text_content is VARCHAR(5000); the API refuses longer text with a
// clear message. The count appears from POST_TEXT_WARN_CHARS so it is not
// clutter on ordinary posts.
const POST_TEXT_MAX_CHARS = 5000;
const POST_TEXT_WARN_CHARS = 4500;

interface PostsGridProps {
  posts: Post[];
  /** A fetch is in flight. The toolbar stays put and the spinner takes the
   *  card area — see the loading block below. */
  isLoading?: boolean;
  filterChild: string | null;
  selectedPost: Post | null;
  setSelectedPost: (post: Post | null, skipConfirmDiscard?: boolean) => void;
  setPosts: React.Dispatch<React.SetStateAction<Post[]>>;
  onNewPost: () => void;
  onReloadPosts: (skipConfirm?: boolean) => Promise<void>;
  /** true when the grid is showing deleted posts rather than live ones */
  showingDeleted: boolean;
  onToggleDeleted: (next: boolean) => Promise<void>;
  onViewReport: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSaveSuccess: (message: string) => void;
  weekStart: string | null;
  filterToDate: string | null;
  onWeekChange: (monday: string, friday: string) => void;
  shortcut: DateShortcut;
  setShortcut: (v: DateShortcut) => void;
  setFilterFromDate: (v: string | null) => void;
  setFilterToDate: (v: string | null) => void;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  sortOrder: 'asc' | 'desc';
  setSortOrder: (v: 'asc' | 'desc') => void;
}

const PostsGrid: React.FC<PostsGridProps> = ({
  posts,
  isLoading = false,
  filterChild,
  selectedPost,
  setSelectedPost,
  setPosts,
  onNewPost,
  onReloadPosts,
  showingDeleted,
  onToggleDeleted,
  onViewReport,
  onDirtyChange,
  onSaveSuccess,
  weekStart,
  filterToDate,
  onWeekChange,
  shortcut,
  setShortcut,
  setFilterFromDate,
  setFilterToDate,
  searchTerm,
  setSearchTerm,
  sortOrder,
  setSortOrder,
}) => {
  const applyShortcut = (type: DateShortcut) => {
    setShortcut(type);
    const now = new Date();
    if (type === 'today') {
      const t = toDateString(now);
      setFilterFromDate(t);
      setFilterToDate(t);
    } else if (type === 'week') {
      setFilterFromDate(toDateString(getMonday(now)));
      setFilterToDate(toDateString(getFriday(now)));
    }
  };
  const { user, userRoleIds, roles, childProfiles, selectedOrganisation, activeUser } = useAppState();
  // Prefer the PIN-active user's flag (the actual current actor); fall back
  // to the device-session (Cognito) user's flag if no active user is set or
  // an older token didn't carry isExternal.
  const isExternal =
    activeUser?.isExternal === true ||
    (activeUser?.isExternal === undefined && selectedOrganisation?.isExternal === true);
  const canPublish = userRoleIds.includes(roles.Reporting);

  // Who may delete the open post. Read by the footer in BOTH modes so the
  // viewing and editing paths cannot drift apart on permissions — it is the
  // same condition that gates entry to edit mode in the first place.
  /** May the open post be edited by whoever is looking at it? Exactly the
   *  condition the Edit button uses — stated once, because click-to-edit and the
   *  button must never disagree about whether editing is possible. */
  const canEditSelectedPost =
    !showingDeleted
    && !!selectedPost
    && !selectedPost.is_published
    && userRoleIds.includes(roles.Editor)
    && (!isExternal || selectedPost.created_by === (activeUser?.id ?? user?.id));

  /** Bumped when someone clicks the read-only post but cannot edit it: the Edit
   *  button is not there to point at, so the Locked badge answers instead. */
  const [lockedFlash, setLockedFlash] = useState(0);

  /** A click on the post's own content — its words or its photos — means "let me
   *  change this". Where that is allowed it simply starts editing: entering edit
   *  mode alters nothing, Cancel is right there, and the unsaved-changes guard
   *  covers the moment anything is actually typed. A confirmation for a harmless,
   *  reversible act is the kind of dialog people learn to click through, which
   *  would cost us the ones that matter (publish, delete, the scope choice).
   *
   *  Where it is NOT allowed, the same gesture flashes the Locked badge rather
   *  than doing nothing — silence reads as the app being broken, and the footer
   *  already carries "Why can't I edit this post?". */
  const handleContentClick = () => {
    if (isEditing) return;
    if (canEditSelectedPost) { setIsEditing(true); return; }
    if (selectedPost?.is_published) setLockedFlash(n => n + 1);
  };

  const canDeleteSelectedPost =
    !showingDeleted &&
    !!selectedPost &&
    selectedPost.id !== -1 &&
    !selectedPost.is_published &&
    userRoleIds.includes(roles.Editor) &&
    (!isExternal || selectedPost.created_by === (activeUser?.id ?? user?.id));

  // The support reference: not on unsaved posts (no id yet) or deleted ones
  // (support can't open those).
  const showPostRef = !showingDeleted && !!selectedPost && selectedPost.id !== -1;

  // ── Search state ──
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ── Local reload spinning state ──
  const [spinning, setSpinning] = useState(false);

  const selectedCardRef = useRef<HTMLDivElement>(null);
  const postsPaneRef = useRef<HTMLDivElement>(null);
  const detailPanelRef = useRef<HTMLDivElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [editDate, setEditDate] = useState<string | null>(null);
  const [editTime, setEditTime] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  // Counted in characters (code points), as the database counts them.
  const editTextChars = useMemo(() => Array.from(editText).length, [editText]);
  const editTextTooLong = editTextChars > POST_TEXT_MAX_CHARS;
  const [isSaving, setIsSaving] = useState(false);
  const offlineTitle = useOfflineActionTitle();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // How many posts are still inside their restore window. The "Recently
  // deleted" link only appears when this is non-zero — an entry point to an
  // empty list is just clutter on a screen most people use every day.
  // Failing to fetch leaves it at 0, so a hidden link is the safe default: the
  // posts themselves are unaffected and the window is 7 days long.
  const [deletedPostCount, setDeletedPostCount] = useState(0);
  // The restore window, as the SERVER reports it. POST_DELETION_GRACE_DAYS is
  // the server's to define — the sweep enforces it — so the confirmation copy
  // reads it back rather than restating a number that could drift out of step
  // with what actually happens.
  const [graceDays, setGraceDays] = useState(7);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [newPhotos, setNewPhotos] = useState<{ file: File; redactedFile?: File; previewUrl: string }[]>([]);
  const [uploadsToDelete, setUploadsToDelete] = useState<Set<number>>(new Set());
  const [isDragOver, setIsDragOver] = useState(false);
  // Index of the new photo currently open in the face-blur editor (null = closed).
  const [redactIndex, setRedactIndex] = useState<number | null>(null);
  // What is open full size in the editor's viewer. A photo added in this sitting
  // needs it as much as a saved one — arguably more, since checking the blur
  // before saving is the whole point — so the viewer takes either.
  type Viewing = { kind: 'upload'; id: number } | { kind: 'new'; index: number };
  const [viewing, setViewing] = useState<Viewing | null>(null);
  // Which way round the photo in the viewer is. Null until it has loaded — the
  // element fills its long side, so it hugs the image instead of letterboxing
  // inside a box the shadow is drawn around.
  const [viewerPortrait, setViewerPortrait] = useState<boolean | null>(null);
  // The photo awaiting confirmation of "remove the blur" — either kind, since
  // an unsaved blur is work worth a question too. The viewer stays open behind
  // the question and refreshes to the original once it is answered.
  const [confirmRemoveBlur, setConfirmRemoveBlur] = useState<Viewing | null>(null);
  // Upload id of the EXISTING (already-uploaded) image open in the blur editor, or null.
  const [redactUploadId, setRedactUploadId] = useState<number | null>(null);
  // Pending blur edits to existing uploads, keyed by upload id:
  //   redactedFile / previewUrl – a freshly redacted image to upload as the modified variant on save
  //   display_modified          – switch which variant is shown (no re-upload needed)
  //   removeModified            – drop the modified variant, revert to the original only
  const [uploadEdits, setUploadEdits] = useState<Record<number, { redactedFile?: File; previewUrl?: string; display_modified?: boolean; removeModified?: boolean }>>({});

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // ── Sync detail panel when selectedPost changes ──
  useEffect(() => {
    if (selectedPost) {
      setEditText(selectedPost.text_content || "");
      setIsEditing(selectedPost.id === -1);
      setIsDirty(false);
      setNewPhotos([]);
      setUploadsToDelete(new Set());
      setShowInsights(false);
      try {
        const d = new Date(selectedPost.post_date);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        setEditDate(`${yyyy}-${mm}-${dd}`);
        setEditTime(`${hh}:${min}`);
      } catch {
        setEditDate(null);
        setEditTime(null);
      }
    } else {
      // Post closed/deselected — clear the edit state so a stale "dirty" flag
      // doesn't linger and get re-reported to the parent, which would
      // re-trigger the unsaved-changes prompt the next time a post is opened.
      setIsEditing(false);
      setIsDirty(false);
      setNewPhotos([]);
      setUploadsToDelete(new Set());
      setShowInsights(false);
    }
    // Either way — a different post, or none — pending photo edits belong to the
    // post that is going, not the one arriving.
    setUploadEdits(prev => {
      if (Object.keys(prev).length === 0) return prev;
      Object.values(prev).forEach(e => { if (e.previewUrl) URL.revokeObjectURL(e.previewUrl); });
      return {};
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPost?.id]);

  // Bring the open post fully into view. Measured against the grid pane rather
  // than left to scrollIntoView: `block: 'center'` clips a card taller than the
  // pane at both ends, and scrollIntoView is free to scroll ancestors too.
  // Scrolls by the smallest amount that shows the whole card, and does nothing
  // if it's already visible.
  useEffect(() => {
    if (!selectedPost) return;
    let cancelled = false;
    let frame = 0;

    const measureAndScroll = () => {
      if (cancelled) return;
      const card = selectedCardRef.current;
      const pane = postsPaneRef.current;
      if (!card || !pane) return;

      const cardBox = card.getBoundingClientRect();
      const paneBox = pane.getBoundingClientRect();
      // Pane is hidden (narrow screens hand the whole area to the drawer).
      if (paneBox.height === 0) return;

      const margin = 16;
      const tallerThanPane = cardBox.height + margin * 2 > paneBox.height;
      let delta = 0;

      if (cardBox.top - margin < paneBox.top) {
        delta = cardBox.top - margin - paneBox.top;
      } else if (cardBox.bottom + margin > paneBox.bottom) {
        // If the card can't fit whole, show it from the top rather than the end.
        delta = tallerThanPane
          ? cardBox.top - margin - paneBox.top
          : cardBox.bottom + margin - paneBox.bottom;
      }

      if (delta === 0) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      pane.scrollBy({ top: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    // Opening the drawer narrows the pane and reflows the grid, so the card's
    // final position isn't known until the drawer has finished arriving.
    // Waiting on the panel's own animation ties this to how long that actually
    // takes rather than a guessed delay; switching between already-open posts
    // resolves immediately, since nothing re-animates.
    const panel = detailPanelRef.current;
    const animations = panel?.getAnimations?.() ?? [];

    if (animations.length) {
      Promise.all(animations.map(a => a.finished.catch(() => undefined)))
        .then(() => { frame = requestAnimationFrame(measureAndScroll); });
    } else {
      frame = requestAnimationFrame(measureAndScroll);
    }

    return () => { cancelled = true; cancelAnimationFrame(frame); };
  }, [selectedPost?.id]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (isEditing && selectedPost?.id === -1) {
      setTimeout(() => textAreaRef.current?.focus(), 100);
    }
  }, [isEditing, selectedPost]);

  useEffect(() => {
    onDirtyChange?.(isDirty || newPhotos.length > 0 || uploadsToDelete.size > 0);
  }, [isDirty, newPhotos, uploadsToDelete, onDirtyChange]);

  /** Changes made in this sitting that a save would write: text, photos added,
   *  photos removed, blurs applied or removed. Nothing in the app said so — the
   *  only sign was the Save button ceasing to be greyed out, which is a poor way
   *  to learn that a blur you applied is not yet anywhere. */
  const hasPendingChanges =
    isDirty
    || newPhotos.length > 0
    || uploadsToDelete.size > 0
    || Object.keys(uploadEdits).length > 0;

  // ── Detail panel handlers ──
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) processFiles(Array.from(e.target.files));
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) processFiles(Array.from(e.dataTransfer.files));
  };

  const processFiles = (files: File[]) => {
    const entries = files.filter(f => f.type.startsWith('image/')).map(f => ({ file: f, previewUrl: URL.createObjectURL(f) }));
    setNewPhotos(prev => [...prev, ...entries]);
    setIsDirty(true);
  };

  const removeNewPhoto = (index: number) => {
    setNewPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].previewUrl);
      updated.splice(index, 1);
      return updated;
    });
    setIsDirty(true);
  };

  const markUploadForDeletion = (uploadId: number) => {
    setUploadsToDelete(prev => { const n = new Set(prev); n.add(uploadId); return n; });
    setIsDirty(true);
  };

  const undoMarkForDeletion = (uploadId: number) => {
    setUploadsToDelete(prev => { const n = new Set(prev); n.delete(uploadId); return n; });
  };

  // Esc closes the full-size viewer, as it does the blur editor and the PDF
  // viewer. Bound only while it is open, so it cannot swallow anything else.
  useEffect(() => {
    if (viewing === null) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setViewing(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [viewing]);

  // A different photo measures again.
  useEffect(() => { setViewerPortrait(null); }, [viewing]);

  const handleSave = async () => {
    // Checked before any photo is uploaded, so a refused save leaves nothing behind in S3.
    if (!selectedPost || editTextTooLong) return;
    setIsSaving(true);
    try {
      let newPostDateISO = selectedPost.post_date;
      if (editDate) {
        const local = new Date(`${editDate}T${editTime || '00:00'}`);
        newPostDateISO = local.toISOString();
      }

      const childUniqueId = childProfiles.find(c => c.id === selectedPost.child_ids[0])?.unique_id || 'XYZ';

      // ── New photos: upload original always, modified variant if blurred ──
      const processedUploads: Upload[] = [];
      for (const photo of newPhotos) {
        const { file, redactedFile } = photo;
        const baseName = makeBaseName(childUniqueId, newPostDateISO);

        const s3_key = await putImage(file, `${baseName}.jpg`, 800, 0.75, childUniqueId);
        const s3_key_thumbnail = await putImage(file, `${baseName}_thumb.jpg`, 200, 0.5, childUniqueId);

        let modified_s3_key: string | null = null;
        let modified_s3_key_thumbnail: string | null = null;
        if (redactedFile) {
          const m = await putModifiedVariant(redactedFile, baseName, childUniqueId);
          modified_s3_key = m.modified_s3_key;
          modified_s3_key_thumbnail = m.modified_s3_key_thumbnail;
        }

        processedUploads.push({
          id: -1,
          file_name: file.name,
          file_type: 'image/jpeg',
          s3_key,
          s3_key_thumbnail,
          modified_s3_key,
          modified_s3_key_thumbnail,
          display_modified: true,
          upload_type_id: 1,
          uploaded_at: new Date().toISOString(),
          url: '',
          thumbnail_url: ''
        });
      }

      // ── Existing uploads: apply blur edits (apply / switch / remove) ─────
      const processedExisting: Upload[] = [];
      for (const u of (selectedPost.uploads || [])) {
        const edit = uploadEdits[u.id];
        let modified_s3_key = u.modified_s3_key ?? null;
        let modified_s3_key_thumbnail = u.modified_s3_key_thumbnail ?? null;
        let display_modified = edit?.display_modified ?? u.display_modified ?? true;

        if (edit?.redactedFile) {
          const m = await putModifiedVariant(edit.redactedFile, makeBaseName(childUniqueId, newPostDateISO), childUniqueId);
          modified_s3_key = m.modified_s3_key;
          modified_s3_key_thumbnail = m.modified_s3_key_thumbnail;
          display_modified = true;
        } else if (edit?.removeModified) {
          modified_s3_key = null;
          modified_s3_key_thumbnail = null;
          display_modified = true;
        }

        processedExisting.push({
          ...u,
          to_delete: uploadsToDelete.has(u.id),
          modified_s3_key,
          modified_s3_key_thumbnail,
          display_modified,
        });
      }

      const finalUploads: Upload[] = [...processedExisting, ...processedUploads];

      const updated: Post = {
        ...selectedPost,
        text_content: editText,
        post_date: newPostDateISO,
        updated_at: new Date().toISOString(),
        uploads: finalUploads
      };

      // Every staged photo change, released together once the save has landed.
      //
      // New photos must go or they'd show again as "New" tiles alongside the
      // now-saved uploads. uploadsToDelete must go for a subtler reason: the
      // dirty signal is `isDirty || newPhotos.length || uploadsToDelete.size`,
      // so a set left populated after a successful save keeps the post marked
      // dirty for ever — and closing it prompts about unsaved changes that were
      // saved. handleCancelEdit already cleared both; only save had the gap,
      // which is why they now reset in one place instead of two lists that have
      // to be kept in step by hand.
      const clearStagedPhotoEdits = () => {
        newPhotos.forEach(p => URL.revokeObjectURL(p.previewUrl));
        setNewPhotos([]);
        setUploadsToDelete(new Set());
      };

      if (selectedPost.id === -1) {
        updated.title = updated.post_date;
        await PostDataService.createPost(updated);
        setIsEditing(false);
        setIsDirty(false);
        clearStagedPhotoEdits();
        setSelectedPost(null, true);
        await onReloadPosts(true);
        onSaveSuccess('Post created successfully!');
      } else {
        await PostDataService.updatePost(selectedPost.id, updated, selectedPost.updated_at);
        setIsEditing(false);
        setIsDirty(false);
        // Release any pending blur-preview object URLs and clear the edit buffers.
        Object.values(uploadEdits).forEach(e => e.previewUrl && URL.revokeObjectURL(e.previewUrl));
        setUploadEdits({});
        clearStagedPhotoEdits();
        await onReloadPosts(true);
        onSaveSuccess('Post updated successfully!');
      }
    } catch (err: any) {
      console.error("Failed to save post:", err);
      const conflict = getPostConflict(err);
      if (conflict) {
        alert(`This post was edited${conflict.updated_by_name ? ` by ${conflict.updated_by_name}` : ''} since you opened it. Reload to see the latest before saving again.`);
      } else if (err.response?.data?.code === 'post_text_too_long') {
        // The server counts after swapping names for placeholders, so this can
        // happen just under the limit shown here.
        alert(err.response.data.error);
      } else if (err.response?.status === 403) {
        alert("You do not have permission to edit or create posts for this child.");
      } else {
        alert("Failed to save post. Please try again.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Cheap count query behind the "Recently deleted" link. Refreshed whenever
  // the answer can have changed — on mount, after a delete, and after the
  // restore modal closes — so the link appears the moment there is something to
  // restore and disappears when the last one is put back.
  const refreshDeletedCount = React.useCallback(async () => {
    if (!userRoleIds.includes(roles.Editor)) return;
    // Scoped to the child on screen, because that is what the restore view
    // shows. Counting the whole organisation put "Recently deleted (3)" above a
    // child with none of their own, and the way back led to an empty grid.
    // No child chosen means no view to offer, so there is nothing to count.
    if (!filterChild) { setDeletedPostCount(0); return; }
    try {
      const { posts: deleted, graceDays: days } = await PostDataService.getDeletedPosts(filterChild);
      setDeletedPostCount(deleted.length);
      if (days) setGraceDays(days);
    } catch {
      setDeletedPostCount(0);
    }
  }, [userRoleIds, roles.Editor, filterChild]);

  useEffect(() => { refreshDeletedCount(); }, [refreshDeletedCount]);

  const [isRestoring, setIsRestoring] = useState(false);

  /** Whole days left, rounded up — "0 days left" would read as already gone. */
  const daysLeftToRestore = (deletedAt?: string | null): number | null => {
    if (!deletedAt) return null;
    const expires = new Date(deletedAt).getTime() + graceDays * 86_400_000;
    return Math.max(1, Math.ceil((expires - Date.now()) / 86_400_000));
  };

  const handleRestorePost = async () => {
    if (!selectedPost || selectedPost.id === -1) return;
    setIsRestoring(true);
    try {
      const result = await PostDataService.restorePost(selectedPost.id);
      setSelectedPost(null, true);
      await onToggleDeleted(true);   // refresh the deleted view; it may now be empty
      await refreshDeletedCount();   // and the badge behind the toggle
      // Images deleted separately keep their own clock and do NOT come back
      // with the post. Saying so is the difference between the user going to
      // restore them and quietly losing them.
      onSaveSuccess(
        result.imagesStillDeleted > 0
          ? `Post restored — ${result.imagesStillDeleted} image${result.imagesStillDeleted === 1 ? '' : 's'} ` +
            `on it were deleted separately and did not come back. Open the post and choose Edit to restore them.`
          : 'Post restored.'
      );
    } catch (err: any) {
      const expired = isPastRestoreWindow(err);
      alert(expired
        ? 'This post passed its restore window and has already been permanently deleted.'
        : 'Could not restore that post. Please try again.');
      if (expired) await onToggleDeleted(true);
    } finally {
      setIsRestoring(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPost || selectedPost.id === -1) return;
    setIsDeleting(true);
    try {
      await PostDataService.deletePost(selectedPost.id);
      setShowDeleteModal(false);
      setSelectedPost(null);
      await onReloadPosts(true);
      await refreshDeletedCount();
    } catch (err: any) {
      console.error("Failed to delete post:", err);
      alert("Failed to delete post.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Discard the in-progress edit and leave edit mode (shared by the header
  // "Cancel Edit" button and the footer Cancel). For a brand-new post (id === -1)
  // there's nothing to revert to, so close the detail panel instead.
  /** Drops pending photo edits — blurs applied, blurs removed, variant swaps —
   *  and releases any preview URLs they were holding. Cancel and closing the
   *  post both have to do this: these edits live in component state, which
   *  outlives the drawer, so an unsaved blur removal was still sitting there
   *  when the post was reopened and would have gone in on the next save. */
  const discardUploadEdits = () => {
    setUploadEdits(prev => {
      Object.values(prev).forEach(e => { if (e.previewUrl) URL.revokeObjectURL(e.previewUrl); });
      return {};
    });
  };

  const handleCancelEdit = () => {
    if (!selectedPost) return;
    if (selectedPost.id === -1) {
      setSelectedPost(null);
    } else {
      setEditText(selectedPost.text_content || '');
      setIsEditing(false);
      setNewPhotos([]);
      setUploadsToDelete(new Set());
      discardUploadEdits();
    }
    setIsDirty(false);
  };

  const fmtDate = (dateStr: string | null, includeYear = true) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00');
    const parts = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short',
      ...(includeYear && { year: 'numeric' }),
    }).formatToParts(d);
    return parts.map(p => p.value).join('');
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* ── Toolbar ── */}
      <div
        className="d-flex align-items-center"
        style={{
          background: 'transparent',
          borderBottom: '1px solid rgba(38, 146, 63, 0.25)',
          padding: '8px 16px',
          minHeight: '48px',
          flexShrink: 0,
          flexWrap: 'wrap',
          rowGap: '8px',
          overflow: 'visible',
          position: 'relative',
          zIndex: 5,
        }}
      >
        <>
            {/* Static date-range segment. Hidden in the deleted view: it
                spans every period, so a week selector there would be a control
                that changes nothing — worse than absent, because the user would
                reasonably conclude there was nothing older to find. */}
            {!showingDeleted && <div className="pill-toolbar__segment" role="group" aria-label="Date range" style={{ flexShrink: 0 }}>
              <button type="button" className={shortcut === 'today' ? 'is-active' : ''} onClick={() => applyShortcut('today')} aria-pressed={shortcut === 'today'}>Today</button>
              <button type="button" className={shortcut === 'week' ? 'is-active' : ''} onClick={() => applyShortcut('week')} aria-pressed={shortcut === 'week'}>Week</button>
              <button type="button" className={shortcut === 'custom' ? 'is-active' : ''} onClick={() => setShortcut('custom')} aria-pressed={shortcut === 'custom'}>Custom</button>
            </div>}

            {/* Sits where the date pills normally do: the left of the toolbar is
                where you go to change what you are looking at, and leaving the
                deleted view is exactly that. */}
            {showingDeleted && (
              <button
                type="button"
                className="deleted-back-pill"
                onClick={() => onToggleDeleted(false)}
              >
                <i className="fas fa-arrow-left" aria-hidden="true" /> Back to posts
              </button>
            )}

            {/* Contextual control next to the segment.
                "Week" mode renders the shared <WeekNavPill> (same component
                used on Schedule / Attendance / Reports). "Today" and "Custom"
                modes render their own content in a matching .week-nav-pill
                wrapper so the chrome is identical across all three modes. */}
            {showingDeleted ? null : shortcut === 'today' ? (
              <div className="week-nav-pill week-nav-pill--inline" style={{ flexShrink: 0 }}>
                <span className="week-nav-pill__static">{weekStart ? fmtDate(weekStart) : ''}</span>
              </div>
            ) : shortcut === 'week' && weekStart ? (
              <div style={{ flexShrink: 0, marginLeft: 12, display: 'inline-flex' }}>
                <WeekNavPill weekStart={weekStart} onWeekChange={onWeekChange} />
              </div>
            ) : shortcut === 'custom' ? (
              <CustomRangePill
                fromDate={weekStart}
                toDate={filterToDate}
                onFromChange={setFilterFromDate}
                onToChange={setFilterToDate}
                inline
              />
            ) : null}

            {/* The one action on this bar. The divider marks the turn from
                choosing what you're looking at to acting on it. */}
            {userRoleIds.includes(roles.Editor) && (
              <>
                {/* Writing a new post has no meaning while browsing deleted ones. */}
                <div className="pill-toolbar__divider" style={{ margin: '0 14px', flexShrink: 0 }} />
                {!showingDeleted && <button
                  type="button"
                  className="btn-new-post"
                  data-help="posts.new-post"
                  onClick={onNewPost}
                  disabled={!filterChild}
                  title={filterChild ? 'Write a new post' : 'Choose a child first'}
                >
                  <span className="btn-new-post__ring" aria-hidden="true">
                    <i className="fas fa-plus" />
                  </span>
                  <span className="btn-new-post__label">New Post</span>
                </button>}
                {/* Deleting is permanent after a grace period, so the way back
                    has to be findable without knowing it exists — but only when
                    there is a way back to offer. */}
                {/* Entry point only — the ribbon owns the way back, so the
                    mode is explained in one place rather than toggled from two. */}
                {!showingDeleted && deletedPostCount > 0 && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-decoration-none ms-2"
                    data-help="posts.recently-deleted"
                    onClick={() => onToggleDeleted(true)}
                    title="Show posts deleted in the last few days"
                  >
                    <i className="fas fa-trash-arrow-up me-1" />
                    Recently deleted ({deletedPostCount})
                  </button>
                )}
              </>
            )}

            {/* Spacer — pushes the action cluster to the right */}
            <div style={{ flex: 1 }} />

            {/* Right */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexShrink: 0, flexWrap: 'nowrap', gap: '12px' }}>
              {/* Sort order — hidden for now. Toggle still wired up via props
                  if/when we want to bring it back. */}
              {false && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  title={`Sort: ${sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}`}
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  <i className={`fas fa-arrow-${sortOrder === 'desc' ? 'down' : 'up'}`} />
                  {sortOrder === 'desc' ? 'Newest' : 'Oldest'}
                </button>
              )}

              {canPublish && shortcut === 'week' && posts.some(p => p.is_published) && (
                <button
                  className="week-nav-btn toolbar-icon-btn"
                  onClick={onViewReport}
                  title="Report preview"
                  aria-label="Report preview"
                >
                  <i className="fas fa-eye" />
                </button>
              )}
              {/* Search */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search posts…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') { setSearchTerm(''); setSearchOpen(false); } }}
                  style={{
                    width: searchOpen ? '180px' : '0',
                    opacity: searchOpen ? 1 : 0,
                    padding: searchOpen ? undefined : '0',
                    border: searchOpen ? undefined : 'none',
                    transition: 'width 0.25s ease, opacity 0.2s ease',
                    overflow: 'hidden',
                    flexShrink: 0,
                    pointerEvents: searchOpen ? 'auto' : 'none',
                  }}
                />
                <button
                  className="week-nav-btn"
                  onClick={() => {
                    if (searchOpen) { setSearchTerm(''); setSearchOpen(false); }
                    else setSearchOpen(true);
                  }}
                  title={searchOpen ? 'Close search' : 'Search posts'}
                >
                  <i className={`fas ${searchOpen ? 'fa-times' : 'fa-search'}`} />
                </button>
              </div>
              <button
                className="week-nav-btn"
                onClick={async () => { setSpinning(true); try { await onReloadPosts(true); } finally { setSpinning(false); } }}
                disabled={spinning}
                title="Reload posts"
              >
                <i className="fas fa-sync-alt" style={spinning ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined} />
              </button>
            </div>
          </>
      </div>

      {/* ── Deleted-view ribbon ──
          A mode this consequential is marked, not implied. Every card below is
          on its way out, and a user who wandered in and started reading needs
          to know that before they wonder why Edit has vanished. Full width and
          clay-toned: the palette already reserves clay for destructive weight,
          so it reads as "on the way out" without shouting. The way back lives
          here too. The way back sits in the toolbar above, where the date
          controls normally are. */}
      {showingDeleted && (
        <div className="deleted-ribbon" role="status">
          <div className="deleted-ribbon__text">
            <i className="fas fa-trash-can" aria-hidden="true" />
            <span>
              <strong>Showing deleted posts.</strong>{' '}
              These are permanently deleted after {graceDays} days, along with
              their photographs. Open one and choose Restore to keep it.
            </span>
          </div>
        </div>
      )}

      {/* ── Content area ── */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>

        {/* Post grid — one layout throughout. Opening a post narrows this pane
            rather than switching it to a different list, so the cards you were
            just reading stay the cards you're looking at; they simply reflow
            into fewer columns beside the drawer. */}
        <div
          ref={postsPaneRef}
          className={`layout-scrollable-content posts-pane ${selectedPost ? 'has-selection' : ''}`}
          style={{ flex: 1, minWidth: 0, overflowX: 'hidden', height: '100%' }}
        >
          <div className="posts-grid" data-help="posts.list">
            {/* Loading replaces the CARDS, never the screen. Home used to swap the
                whole view for a centred spinner whenever a fetch started with an
                empty list, which tore down this component — toolbar, scroll
                position and all — and rebuilt it a moment later. Worse, it only
                did so when the week you were leaving happened to be empty, so
                the same arrow click behaved differently from week to week. */}
            {isLoading && (
              <div className="posts-empty posts-empty--loading">
                <div className="loading-spinner" />
                <span>Loading posts…</span>
              </div>
            )}
            {!isLoading && posts.length === 0 && (
              <div className="posts-empty">
                No posts for this period.
                {/* Names the control by what's actually on screen — the button
                    shows a plus, not the words "New Post". */}
                {userRoleIds.includes(roles.Editor) && (
                  <> Use the <strong>+</strong> button in the toolbar to write one.</>
                )}
                <div className="mt-3">
                  <HelpAsk guideKey="posts.create-post" label="How do I write a post?" variant="inline" />
                </div>
              </div>
            )}
            {!isLoading && posts.map((p, i) => {
              const isSelected = selectedPost?.id === p.id;
              const shots = (p.uploads || []).filter(u => !u.to_delete);
              const stamp = postStamp(p.post_date);
              return (
                <div
                  key={p.id}
                  ref={isSelected ? selectedCardRef : undefined}
                  className={`card post-card post-card--grid position-relative ${isSelected ? 'selected' : ''} ${selectedPost && !isSelected ? 'is-dimmed' : ''} ${p.is_published ? 'post-published' : 'post-draft'}`}
                  style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}
                >
                  <div className="post-card__click" onClick={() => setSelectedPost(p)}>
                    {shots.length > 0 && (
                      <div className={`post-card__sheet is-${Math.min(shots.length, 2)}`}>
                        {shots.slice(0, 2).map((upload) => (
                          // Full-size (800px) image, not the 200px thumbnail —
                          // the strip is the card's hero and the thumbnail is
                          // both too small and too heavily compressed for it.
                          // The first few cards load eagerly so the top of the
                          // grid paints immediately; everything below waits
                          // until it's scrolled near. The strip has a fixed
                          // height, so deferring costs no layout shift.
                          <img
                            key={upload.id}
                            src={upload.url || upload.thumbnail_url}
                            alt={upload.file_name}
                            loading={i < 4 ? 'eager' : 'lazy'}
                            decoding="async"
                          />
                        ))}
                        {shots.length > 2 && (
                          <span className="post-card__count">+{shots.length - 2}</span>
                        )}
                      </div>
                    )}
                    <div className="post-card__head">
                      {/* On this page posts are just posts — the publishing
                          concept lives on the Reports screen. A published post
                          simply reads as locked. */}
                      {p.is_published && (
                        <span className="post-status-badge locked" title="Locked for reporting">
                          <i className="fas fa-lock" aria-hidden="true" /> Locked
                        </span>
                      )}
                      <span className="post-card__day">{stamp.day}</span>
                      <span className="post-card__time">{stamp.time}</span>
                    </div>
                    {/* Full text, deliberately untruncated — the card clips it
                        to the height its row leaves over and fades the cut, so
                        a post with no photos shows as much as it can fit. */}
                    <p className="post-card__body">{p.text_content}</p>
                  </div>
                  {/* The deadline belongs on the card, not only in the detail
                      panel. Scanning a grid of deleted posts, "which of these
                      am I about to lose" is the question being asked, and
                      answering it per card means not opening each one to find
                      out. Turns red at two days. */}
                  {showingDeleted && daysLeftToRestore(p.deleted_at) !== null && (
                    <div className={`post-card__deleted-foot ${daysLeftToRestore(p.deleted_at)! <= 2 ? 'is-urgent' : ''}`}>
                      <i className="fas fa-clock" aria-hidden="true" />
                      <span>
                        {daysLeftToRestore(p.deleted_at)} day
                        {daysLeftToRestore(p.deleted_at) === 1 ? '' : 's'} left
                      </span>
                      {p.deleted_by_name && (
                        <span className="post-card__deleted-by">· {p.deleted_by_name}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Detail panel (slides in from right) ── */}
        {selectedPost && (
        <div
          ref={detailPanelRef}
          className="detail-panel detail-panel--post card detail-panel-slide"
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <>
              {/* Header and the status strips are fixed chrome — they name what
                  you're looking at, so they stay put while the post scrolls
                  under them. */}
                <div className="card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    {isEditing ? (
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="date"
                          className="form-control"
                          style={{ maxWidth: '150px' }}
                          value={editDate || ''}
                          onChange={(e) => { setEditDate(e.target.value || null); setIsDirty(true); }}
                        />
                        <input
                          type="time"
                          className="form-control"
                          style={{ maxWidth: '120px' }}
                          value={editTime || ''}
                          onChange={(e) => { setEditTime(e.target.value || null); setIsDirty(true); }}
                        />
                      </div>
                    ) : (
                      <div className="post-panel__stamp">
                        <span className="post-panel__day">{postStamp(selectedPost.post_date).dayFull}</span>
                        <span className="post-panel__time">{postStamp(selectedPost.post_date).time}</span>
                      </div>
                    )}
                    <div className="d-flex gap-2">
                      {/* Edit alone. Delete lives bottom-left in the footer in
                          both modes — the routine action and the irreversible
                          one should not sit side by side as a matched pair, and
                          a destructive control that moves depending on mode is
                          one a user has to hunt for. Solid rather than outlined:
                          this is the action taken many times a day. */}
                      {!isEditing && canEditSelectedPost && (
                        <button className="btn btn-primary btn-sm" onClick={() => setIsEditing(true)} title="Edit post">
                          <i className="fas fa-pen me-1"></i> Edit
                        </button>
                      )}
                      {SHOW_AI_INSIGHTS && !isEditing && selectedPost.id !== -1 && (
                        <button
                          className="btn btn-sm"
                          onClick={() => setShowInsights(true)}
                          title="AI insights"
                          style={{
                            background: 'rgba(99, 102, 241, 0.08)',
                            color: '#6366f1',
                            border: '1px solid rgba(99, 102, 241, 0.25)',
                            transition: 'background 0.2s, color 0.2s, border-color 0.2s',
                          }}
                          onMouseEnter={e => {
                            const el = e.currentTarget as HTMLButtonElement;
                            el.style.background = 'linear-gradient(135deg, #6366f1, #8b5cf6)';
                            el.style.color = '#ffffff';
                            el.style.borderColor = 'transparent';
                          }}
                          onMouseLeave={e => {
                            const el = e.currentTarget as HTMLButtonElement;
                            el.style.background = 'rgba(99, 102, 241, 0.08)';
                            el.style.color = '#6366f1';
                            el.style.borderColor = 'rgba(99, 102, 241, 0.25)';
                          }}
                        >
                          <i className="fas fa-wand-magic-sparkles"></i>
                        </button>
                      )}
                      {isEditing && (
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleCancelEdit}
                          title="Cancel editing"
                        >
                          Cancel Edit
                        </button>
                      )}
                      <button
                        className="close-button ms-2"
                        onClick={() => setSelectedPost(null)}
                        title="Close post"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Creator info strip, with the reference to quote to support at
                    the far right. */}
                {(selectedPost.created_by_name || showPostRef) && (
                  <div className="post-meta-strip">
                    {selectedPost.created_by_name && (
                      <>
                        <i className="fas fa-user-edit" />
                        <span>Created by <strong>{selectedPost.created_by_name}</strong></span>
                      </>
                    )}
                    {showPostRef && <SupportRef code={postRef(selectedPost.id)} className="post-meta-strip__ref" dataHelp="posts.support-ref" />}
                  </div>
                )}

                {/* ── Deleted ribbon ──
                    Sits below the attribution, closest to the post itself, so
                    the strips read as a widening funnel: who wrote it, then
                    what is about to happen to it. Same clay palette and same
                    two-day switch to red as the card's footer in the grid, so a
                    card and its open drawer say the same thing in the same
                    voice. Restore lives here too: the only action a deleted
                    post has, next to the deadline that makes it urgent. */}
                {showingDeleted && daysLeftToRestore(selectedPost.deleted_at) !== null && (
                  <div className={`post-meta-strip is-deleted ${daysLeftToRestore(selectedPost.deleted_at)! <= 2 ? 'is-urgent' : ''}`}>
                    <i className="fas fa-trash-can" aria-hidden="true" />
                    <span className="post-meta-strip__text">
                      Deleted{selectedPost.deleted_by_name ? <> by <strong>{selectedPost.deleted_by_name}</strong></> : ''}
                      {' · '}
                      <strong>
                        {daysLeftToRestore(selectedPost.deleted_at)} day
                        {daysLeftToRestore(selectedPost.deleted_at) === 1 ? '' : 's'} left
                      </strong>{' '}to restore
                    </span>
                    {selectedPost.id !== -1 && (
                      <button
                        className="post-meta-strip__action"
                        data-help="posts.restore"
                        disabled={isRestoring}
                        onClick={handleRestorePost}
                      >
                        <i className="fas fa-trash-arrow-up me-1" />
                        {isRestoring ? 'Restoring…' : 'Restore Post'}
                      </button>
                    )}
                  </div>
                )}

                {/* Locked strip — only locked (published) posts get one; an
                    ordinary post is just an editable post, no status needed.
                    The publishing vocabulary lives on the Reports screen. */}
                {!isEditing && selectedPost.is_published && (
                  <div
                    key={`locked-${lockedFlash}`}
                    className={`post-meta-strip is-locked${lockedFlash > 0 ? ' is-flashing' : ''}`}
                  >
                    <span className="post-status-badge locked">
                      <i className="fas fa-lock" aria-hidden="true" /> Locked
                    </span>
                    <span>
                      Locked for reporting
                      {selectedPost.published_by_name && <> by <strong>{selectedPost.published_by_name}</strong></>}
                      {selectedPost.published_at && (
                        <> on {new Date(selectedPost.published_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                      )}
                    </span>
                  </div>
                )}

                {/* Everything below the chrome scrolls. */}
                <div className="post-panel__scroll">
                <div className="card-body">
                  {isEditing ? (
                    <>
                      <textarea
                        ref={textAreaRef}
                        className="form-control mb-2 post-text-area"
                        data-help="posts.editor"
                        rows={10}
                        value={editText}
                        aria-describedby={editTextChars >= POST_TEXT_WARN_CHARS ? 'post-text-count' : undefined}
                        onChange={(e) => {
                          setEditText(e.target.value);
                          setIsDirty(e.target.value !== (selectedPost?.text_content || ''));
                        }}
                      />
                      {editTextChars >= POST_TEXT_WARN_CHARS && (
                        <div
                          id="post-text-count"
                          className={`post-text-count${editTextTooLong ? ' is-over' : ''}`}
                          role={editTextTooLong ? 'alert' : undefined}
                        >
                          {editTextTooLong
                            ? `Too long by ${(editTextChars - POST_TEXT_MAX_CHARS).toLocaleString('en-GB')} characters. Shorten it to save.`
                            : `${editTextChars.toLocaleString('en-GB')} / ${POST_TEXT_MAX_CHARS.toLocaleString('en-GB')} characters`}
                        </div>
                      )}

                      {/* AI spell/grammar correction — works for both saved and
                          unsaved (id === -1) posts via the body-based
                          POST /posts/correct endpoint. Keyed by post id so
                          suggestion state resets when switching posts. */}
                      <FixTypos
                        key={selectedPost.id}
                        text={editText}
                        postId={selectedPost.id !== -1 ? selectedPost.id : undefined}
                        childIds={selectedPost.child_ids ?? []}
                        onAccept={(corrected) => {
                          setEditText(corrected);
                          setIsDirty(corrected !== (selectedPost?.text_content || ''));
                        }}
                      />
                    </>
                  ) : (
                    <p
                      className={`post-panel__text${canEditSelectedPost ? ' is-clickable' : ''}`}
                      onClick={handleContentClick}
                      title={canEditSelectedPost ? 'Click to edit this post' : undefined}
                    >
                      {selectedPost.text_content}
                    </p>
                  )}

                  {isEditing && (
                    <div className="mb-3">
                      <label className="form-label">Photos</label>
                      <div
                        className={`photo-drag-and-drop-area p-4 text-center mb-3 ${isDragOver ? 'selected' : ''}`}
                        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={handleDrop}
                        data-help="posts.photos"
                        onClick={() => document.getElementById('fileInput')?.click()}
                      >
                        <i className="fas fa-cloud-upload-alt fa-2x mb-2"></i>
                        <p className="mb-0">Drag and drop photos here, or click to choose</p>
                        <input type="file" id="fileInput" multiple accept="image/*" className="d-none" onChange={handleFileSelect} />
                      </div>
                    </div>
                  )}

                  {isEditing && (
                    <div className="images d-flex flex-wrap gap-2">
                      {selectedPost.uploads?.map((u) => {
                        const isDeletedUpload = uploadsToDelete.has(u.id);
                        const edit = uploadEdits[u.id];
                        // A pending removal counts: without it the tile still
                        // offered "Remove blur" on a photo already showing its
                        // original, which reads as the removal not having worked.
                        const hasModified = (!!u.has_modified || !!edit?.redactedFile)
                          && !edit?.removeModified;
                        // Which variant the tile currently shows (respecting pending edits).
                        // Full-size throughout — the 150px tiles outrun the 200px
                        // thumbnail on a 2× screen, and blur decisions get made here.
                        let thumbSrc = u.url || u.thumbnail_url;
                        if (edit?.previewUrl) thumbSrc = edit.previewUrl;
                        else if (edit?.removeModified) thumbSrc = u.original_url || u.url || u.thumbnail_url;
                        else if (edit?.display_modified === true) thumbSrc = u.modified_url || u.url || u.thumbnail_url;
                        else if (edit?.display_modified === false) thumbSrc = u.original_url || u.url || u.thumbnail_url;
                        const showingModified = hasModified && !edit?.removeModified &&
                          (edit?.previewUrl ? true : (edit?.display_modified ?? u.display_modified ?? true));
                        return (
                          <div
                            key={u.id}
                            className="photo-tile"
                            style={{ opacity: isDeletedUpload ? 0.55 : 1, filter: isDeletedUpload ? 'grayscale(100%)' : 'none' }}
                          >
                            <img src={thumbSrc} alt={u.file_name} />

                            {!isDeletedUpload && hasModified && (
                              <span
                                className="tile-badge"
                                style={{ background: showingModified ? 'rgba(42,107,58,0.95)' : 'rgba(176,48,48,0.95)' }}
                                title={showingModified ? 'The blurred version is shown' : 'The original is shown (blur hidden)'}
                              >
                                <i className={`fas fa-${showingModified ? 'user-secret' : 'eye'}`}></i>
                                {showingModified ? 'Blurred' : 'Original'}
                              </span>
                            )}

                            {/* Reviewer exclusion is read-only here — it's set/cleared in
                                the Review & Publish workflow, not by Editors. */}
                            {!isDeletedUpload && edit && (
                              <span
                                className="tile-badge tile-badge--unsaved"
                                title="This change is not saved yet"
                              >
                                <i className="fas fa-circle" />
                                {edit.removeModified ? 'Blur removed' : edit.previewUrl ? 'Blurred' : 'Changed'}
                                {' · not saved'}
                              </span>
                            )}

                            {!isDeletedUpload && u.excluded_from_report && (
                              <span
                                className="tile-badge"
                                style={{ top: 'auto', bottom: 6, background: 'rgba(38,36,32,0.85)' }}
                                title={`Excluded from the report by a reviewer${u.exclusion_reason ? ` — ${EXCLUSION_REASON_LABELS[u.exclusion_reason] ?? u.exclusion_reason}` : ''}${u.exclusion_note ? `: “${u.exclusion_note}”` : ''}`}
                              >
                                <i className="fas fa-ban"></i>Excluded
                              </span>
                            )}

                            {/* The photo itself opens full size: a thumbnail is
                                too small to check a blur against, which is the
                                one judgement this screen exists to support. */}
                            {!isDeletedUpload && (
                              <button
                                type="button"
                                className="tile-open"
                                data-help="posts.view-full-size"
                                onClick={(e) => { e.stopPropagation(); setViewing({ kind: 'upload', id: u.id }); }}
                                title="View full size"
                                aria-label={`View ${u.file_name} full size`}
                              />
                            )}

                            <div className="tile-controls">
                              <button
                                type="button"
                                className={`tile-btn tile-close ${isDeletedUpload ? 'tile-btn-primary' : 'tile-btn-danger'}`}
                                onClick={(e) => { e.stopPropagation(); isDeletedUpload ? undoMarkForDeletion(u.id) : markUploadForDeletion(u.id); }}
                                title={isDeletedUpload ? 'Restore photo' : 'Delete photo'}
                              >
                                <i className={`fas fa-${isDeletedUpload ? 'undo' : 'times'}`}></i>
                              </button>

                              {!isDeletedUpload && (
                                <div className="tile-actions">
                                  {!hasModified ? (
                                    <button
                                      type="button" className="tile-btn tile-btn-pill"
                                      onClick={(e) => { e.stopPropagation(); setRedactUploadId(u.id); }}
                                      title="Blur faces in this photo"
                                      data-help="posts.blur"
                                    >
                                      <i className="fas fa-user-secret"></i>Blur faces
                                    </button>
                                  ) : (
                                    // Already blurred: nothing here. Editing that
                                    // blur, removing it, and choosing which
                                    // variant the post shows are all judgements
                                    // about the image, and they live in the
                                    // full-size view where the image can be seen.
                                    // The tile keeps its Blurred badge, which says
                                    // the fact, and the photo is the way in.
                                    null
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {newPhotos.map((photo, index) => (
                        <div key={`new-${index}`} className="photo-tile">
                          <img src={photo.previewUrl} alt="New upload" />
                          {/* Same way in as a saved photo: the thumbnail opens it. */}
                          <button
                            type="button"
                            className="tile-open"
                            data-help="posts.view-full-size"
                            onClick={(e) => { e.stopPropagation(); setViewing({ kind: 'new', index }); }}
                            title="View full size"
                            aria-label="View this photo full size"
                          />
                          <span className="tile-badge" style={{ background: photo.redactedFile ? 'rgba(42,107,58,0.95)' : 'rgba(38,36,32,0.85)' }}
                            title={photo.redactedFile ? 'Faces blurred' : 'New photo — not saved yet'}>
                            <i className={`fas fa-${photo.redactedFile ? 'user-secret' : 'plus'}`}></i>
                            {photo.redactedFile ? 'Blurred' : 'New'}
                          </span>
                          <div className="tile-controls">
                            <button
                              type="button" className="tile-btn tile-close tile-btn-danger"
                              onClick={(e) => { e.stopPropagation(); removeNewPhoto(index); }}
                              title="Remove photo"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                            <div className="tile-actions">
                              {/* Same rule as a saved photo: once it is blurred,
                                  changing that happens in the full-size view. */}
                              {!photo.redactedFile && (
                                <button
                                  type="button" className="tile-btn tile-btn-pill"
                                  onClick={(e) => { e.stopPropagation(); setRedactIndex(index); }}
                                  title="Blur faces"
                                >
                                  <i className="fas fa-user-secret"></i>Blur faces
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Restoring an image changes the post, so it lives behind the
                      same Edit gate as every other change to it — and nothing
                      about deleted images is even visible until Edit is pressed.
                      It sat in the read-only view first, which let a viewer alter
                      a post without entering edit mode: the one thing the Edit
                      button exists to prevent. Renders nothing when the post has
                      no deleted images. */}
                  {isEditing && (
                    <DeletedPostImages
                      postId={selectedPost.id}
                      onRestored={() => onReloadPosts(true)}
                    />
                  )}

                  {!isEditing && (
                    <div
                      className={`images mt-3${canEditSelectedPost ? ' is-clickable' : ''}`}
                      onClick={handleContentClick}
                      title={canEditSelectedPost ? 'Click to edit this post' : undefined}
                    >
                      {selectedPost.uploads?.map((u) => (
                        <div key={u.id} className="post-plate me-2 mb-2">
                          <img
                            src={u.url}
                            style={{ opacity: u.excluded_from_report ? 0.6 : 1 }}
                            alt={u.file_name}
                            loading="lazy"
                            decoding="async"
                          />
                          {u.excluded_from_report && (
                            <span
                              className="badge position-absolute"
                              style={{ bottom: 8, left: 8, background: 'rgba(38,36,32,0.85)' }}
                              title={`Excluded from the report by a reviewer${u.exclusion_reason ? ` — ${EXCLUSION_REASON_LABELS[u.exclusion_reason] ?? u.exclusion_reason}` : ''}${u.exclusion_note ? `: “${u.exclusion_note}”` : ''}`}
                            >
                              <i className="fas fa-ban me-1"></i>Excluded from report
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {redactIndex !== null && newPhotos[redactIndex] && (
                    <PhotoRedactor
                      show
                      file={newPhotos[redactIndex].file}
                      onCancel={() => setRedactIndex(null)}
                      onApply={(redacted) => {
                        const idx = redactIndex;
                        setNewPhotos(prev => {
                          const updated = [...prev];
                          URL.revokeObjectURL(updated[idx].previewUrl);
                          // Keep the ORIGINAL file; store the redacted copy separately so
                          // BOTH are uploaded (original + modified variant) on save.
                          updated[idx] = { ...updated[idx], redactedFile: redacted, previewUrl: URL.createObjectURL(redacted) };
                          return updated;
                        });
                        setIsDirty(true);
                        setRedactIndex(null);
                      }}
                    />
                  )}

                  {redactUploadId !== null && (() => {
                    const u = selectedPost.uploads?.find(x => x.id === redactUploadId);
                    if (!u) return null;
                    // Always edit from the ORIGINAL so re-blurring never compounds.
                    return (
                      <PhotoRedactor
                        show
                        imageUrl={u.original_url || u.url}
                        onCancel={() => setRedactUploadId(null)}
                        onApply={(redacted) => {
                          const id = redactUploadId;
                          setUploadEdits(prev => {
                            const ex = prev[id];
                            if (ex?.previewUrl) URL.revokeObjectURL(ex.previewUrl);
                            return { ...prev, [id]: { redactedFile: redacted, previewUrl: URL.createObjectURL(redacted), display_modified: true, removeModified: false } };
                          });
                          setIsDirty(true);
                          setRedactUploadId(null);
                        }}
                      />
                    );
                  })()}
                </div>
              </div>

              {/* Footer. Nothing here applies to a deleted post — the deadline
                  and Restore live in the ribbon at the top — so rather than
                  leave an empty bar, the drawer simply ends at the post. */}
              {!showingDeleted && (
              <div className="detail-footer-bar post-panel__footer p-3 d-flex justify-content-between">
                {isEditing ? (
                  <>
                    <div>
                      {canDeleteSelectedPost && (
                        <button className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>Delete Post</button>
                      )}
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      {hasPendingChanges && !isSaving && (
                        <span className="unsaved-chip" role="status">
                          <i className="fas fa-circle unsaved-chip__dot" aria-hidden />
                          Unsaved changes
                        </span>
                      )}
                      <button className="btn btn-primary" data-help="posts.save" disabled={!isDirty || isSaving || editTextTooLong || !!offlineTitle} title={offlineTitle} onClick={handleSave}>
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Same slot as in edit mode, so Delete never moves. */}
                    <div>
                      {canDeleteSelectedPost && (
                        <button className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>Delete Post</button>
                      )}
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                      {/* The question, and nothing else. This footer used to
                          carry a sentence of directions to "Reports → Review &
                          Publish" — which is not a place you can navigate to,
                          and which wrapped onto two lines on an iPad. Where
                          publishing happens is the How To's job to explain;
                          this page only has to offer the question, and the
                          question depends on who is asking:
                            · Reporting + published → how to unpublish, change
                              and republish, which is the only route to a change.
                            · Reporting + draft      → how to publish it.
                            · anyone else + published → why it cannot be edited
                              and who can unpublish it. The only thing help can
                              usefully offer someone who cannot act is an
                              explanation and a hand-off.
                            · anyone else + draft    → nothing. They can already
                              edit it; there is no friction to answer. */}
                      {selectedPost.id !== -1 && (canPublish || selectedPost.is_published) && (
                        <HelpAsk
                          className="align-self-center"
                          guideKey={canPublish
                            ? (selectedPost.is_published ? 'posts.unpublish-post' : 'posts.publish-post')
                            : 'posts.locked-post'}
                          label={canPublish
                            ? (selectedPost.is_published ? 'How do I change this post?' : 'How do I publish this post?')
                            : "Why can't I edit this post?"}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
              )}
            </>
        </div>
        )}
      </div>

      {/* ── Full-size photo viewer ──────────────────────────────────────────
          The editor could only ever show thumbnails, so a blur could be applied
          and published without anyone seeing it at full size: the blur editor
          deliberately loads the ORIGINAL (you re-edit from the unblurred source),
          and the only lightbox in the app lives on the Reporting review screen.
          This shows what the post actually displays, and carries every decision
          about the blur — the tile is too small for any of them.

          There is deliberately no "show the original" switch. Keeping a blurred
          copy while publishing the original is a state nobody asks for, and
          anyone wanting to see the unblurred photo can open Re-blur faces, which
          loads exactly that. */}
      {viewing !== null && (() => {
        let src = '';
        let name = '';
        let hasBlur = false;
        let showingBlur = false;

        if (viewing.kind === 'upload') {
          const u = selectedPost?.uploads?.find(x => x.id === viewing.id);
          if (!u) return null;
          const edit = uploadEdits[u.id];
          name = u.file_name;
          hasBlur = (!!u.modified_s3_key || !!edit?.previewUrl) && !edit?.removeModified;
          // display_modified is still honoured — older posts may have been set
          // to show the original — it just cannot be changed from here any more.
          showingBlur = hasBlur && (edit?.previewUrl
            ? true
            : (edit?.display_modified ?? u.display_modified ?? true));
          src = edit?.previewUrl
            ?? (showingBlur ? (u.url ?? '') : (u.original_url || u.url || ''));
        } else {
          const photo = newPhotos[viewing.index];
          if (!photo) return null;
          name = photo.file.name;
          hasBlur = !!photo.redactedFile;
          showingBlur = hasBlur;
          src = photo.previewUrl;
        }

        return (
          <div
            className="photo-viewer"
            role="dialog"
            aria-modal="true"
            aria-label={`${name}, full size`}
            onClick={() => setViewing(null)}
          >
            <button
              type="button"
              className="lightbox-close"
              onClick={() => setViewing(null)}
              title="Close (Esc)"
              aria-label="Close"
            >
              <i className="fas fa-times" />
            </button>

            <img
              className={`photo-viewer__image ${viewerPortrait === null
                ? 'is-measuring'
                : viewerPortrait ? 'is-portrait' : 'is-landscape'}`}
              src={src}
              alt={name}
              onLoad={(e) => {
                const img = e.currentTarget;
                if (!img.naturalWidth || !img.naturalHeight) return;
                setViewerPortrait(img.naturalHeight > img.naturalWidth);
              }}
              onClick={(e) => e.stopPropagation()}
            />

            <div className="photo-viewer__bar" onClick={(e) => e.stopPropagation()}>
              <span className="photo-viewer__what">
                {hasBlur
                  ? (showingBlur
                    ? <><i className="fas fa-user-secret" /> Blurred version — this is what the post shows</>
                    : <><i className="fas fa-image" /> Original — the blurred version is hidden</>)
                  : <><i className="fas fa-image" /> Original — no blurring on this photo</>}
              </span>
              {viewing.kind === 'new' && (
                <span className="photo-viewer__note">Not saved yet</span>
              )}
              {/* Blur controls live here, not on the thumbnail: a 150px tile
                  could not carry them without wrapping, and every one of them is
                  a decision about a photograph nobody could see at that size. */}
              {hasBlur ? (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    data-help="posts.edit-blur"
                    onClick={() => {
                      // Close first: the editor is the taller of the two and
                      // they would otherwise stack.
                      setViewing(null);
                      if (viewing.kind === 'upload') setRedactUploadId(viewing.id);
                      else setRedactIndex(viewing.index);
                    }}
                  >
                    <i className="fas fa-user-secret me-1" />Re-blur faces
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    data-help="posts.remove-blur"
                    onClick={() => setConfirmRemoveBlur(viewing)}
                  >
                    <i className="fas fa-eraser me-1" />Remove blur
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  data-help="posts.blur"
                  onClick={() => {
                    setViewing(null);
                    if (viewing.kind === 'upload') setRedactUploadId(viewing.id);
                    else setRedactIndex(viewing.index);
                  }}
                >
                  <i className="fas fa-user-secret me-1" />Blur faces
                </button>
              )}

            </div>
          </div>
        );
      })()}

      <ConfirmationModal
        show={confirmRemoveBlur !== null}
        title="Remove the blur?"
        message={'The blurred version will be discarded and the post will show the original photo. Blurring it again means starting over.'}
        confirmText="Remove blur"
        onCancel={() => setConfirmRemoveBlur(null)}
        onConfirm={() => {
          const target = confirmRemoveBlur;
          setConfirmRemoveBlur(null);
          if (!target) return;
          if (target.kind === 'upload') {
            setUploadEdits(prev => {
              const ex = prev[target.id];
              if (ex?.previewUrl) URL.revokeObjectURL(ex.previewUrl);
              return { ...prev, [target.id]: { display_modified: true, removeModified: true } };
            });
          } else {
            // An unsaved photo has no saved original to fall back on — the file
            // is still in memory, so the preview goes back to it.
            setNewPhotos(prev => prev.map((photo, i) => {
              if (i !== target.index) return photo;
              URL.revokeObjectURL(photo.previewUrl);
              return { file: photo.file, previewUrl: URL.createObjectURL(photo.file) };
            }));
          }
          setIsDirty(true);
          // The viewer is left open on purpose: it reloads showing the original,
          // which is the confirmation that anything happened at all.
        }}
      />

      {/* Modals */}
      <ConfirmationModal
        show={showDeleteModal}
        title="Delete Post"
        // Deleting is reversible now, and saying otherwise is not a harmless
        // exaggeration: a warning that overstates the stakes teaches people to
        // discount the ones that don't — including the child hard delete, which
        // genuinely cannot be undone. Say what happens, and where the way back is.
        message={`The post and its photographs are removed straight away. You can restore them from Recently deleted for the next ${graceDays} days, after which they are permanently deleted.`}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        isDeleting={true}
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
      />

      {SHOW_AI_INSIGHTS && showInsights && selectedPost && selectedPost.id !== -1 && (
        <AiInsightsPanel post={selectedPost} onClose={() => setShowInsights(false)} />
      )}

    </div>
  );
};

export default PostsGrid;
